import { useCallback, useEffect, useRef, useState } from 'react';
import { Vector2Tuple } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { DragEvent, Polygon, RectDrawControls, useDollhouse } from '@spazious/dollhouse-engine';
import { getNavMeshOutline } from '@spazious/dollhouse-navmesh';
import { useNavigate } from '@spazious/dollhouse-router';
import { FShallowNode } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { DrawFsetupToolModeEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { ROUTE_PATH_INSPECTOR } from '../../constants/index.js';
import { useDelayedSelection, useDrawFsetup } from '../../hooks/index.js';
import { maskAndSizeFromPoints } from '../../utils/index.js';
import { DrawPolygonEvent, PolygonDrawControls } from '../PolygonDrawControls/index.js';

export const DrawFsetupControls = () => {
  const { staging, createAction } = useStaging();
  const set = useStore(({ set }) => set);
  const { node, tool: drawFsetupTool, set: setDrawFsetup, active } = useDrawFsetup();
  const navMeshQuery = useDollhouse(({ world }) => world?.navMeshQuery);
  const navMesh = useDollhouse(({ world }) => world?.navMesh);
  const selector = useRef<string | null>(null);
  const navigate = useNavigate();
  const [fcarpets, setFcarpets] = useState<Vector2Tuple[][] | null>(null);
  const [hoveredFcarpet, setHoveredFcarpet] = useState<number | null>(null);
  const polygonPointsLength = useRef(0);

  const { setSelectors } = useDelayedSelection((pendingSelectors) => {
    if (!pendingSelectors.length) {
      set({
        showNavMesh: false,
        tool: ToolModeEnum.None,
      });
      navigate(ROUTE_PATH_INSPECTOR);
    }
  });

  const handleDraw = useCallback(
    (event: DragEvent) => {
      const { center, size, originalEvent } = event;
      if (!node) {
        console.warn('Trying to draw without selecting a arrangement');
        return;
      }
      switch (originalEvent.type) {
        case 'pointerdown':
          staging.prepare(true);
          const action = createAction('add', node as FShallowNode);
          staging.add(action);
          selector.current = action.payload.selector;
          break;

        case 'pointermove':
          if (!selector.current) {
            return;
          }
          const actions = [];
          actions.push(createAction('update', selector.current, 'position', center));
          actions.push(createAction('update', selector.current, 'attrs.size', size));
          staging.add(actions);

          break;

        case 'pointerup':
          if (staging.stack.length) {
            staging.commit(CommanderActionTypesEnum.Add);
          }
          setDrawFsetup({ node: null });
          if (selector.current) {
            setSelectors([selector.current]);
          } else {
            set({
              showNavMesh: false,
              tool: ToolModeEnum.None,
            });
          }
          selector.current = null;
          break;

        default:
          break;
      }
    },
    [createAction, node, set, setDrawFsetup, setSelectors, staging]
  );

  const applyPolygonPoints = (points: Vector2Tuple[]) => {
    const actions = [];
    const { center, size, mask } = maskAndSizeFromPoints(points);
    actions.push(createAction('update', selector.current!, 'position', center));
    actions.push(createAction('update', selector.current!, 'attrs.size', size));
    actions.push(createAction('update', selector.current!, 'attrs.mask', mask));
    return actions;
  };

  // end polygon
  useEffect(() => {
    const endPolygon = () => {
      if (selector.current) {
        if (polygonPointsLength.current <= 2) {
          // not enough points, remove node
          staging.add(createAction('remove', selector.current));
          selector.current = null;
        }
      }
      if (staging.stack.length) {
        staging.commit(CommanderActionTypesEnum.Add);
      }
      setDrawFsetup({ node: null, tool: DrawFsetupToolModeEnum.Rectangle });
      if (selector.current) {
        setSelectors([selector.current]);
      } else {
        set({
          showNavMesh: false,
          tool: ToolModeEnum.None,
        });
      }
      selector.current = null;
    };
    if (!active && drawFsetupTool === DrawFsetupToolModeEnum.Polygon) {
      endPolygon();
    }
  }, [active, createAction, drawFsetupTool, set, setDrawFsetup, setSelectors, staging]);

  const onDrawPolygon = useEventCallback((event: DrawPolygonEvent) => {
    const { points, originalEvent } = event;
    if (!node) {
      console.warn('Trying to draw without selecting an arrangement');
      return;
    }
    switch (originalEvent.type) {
      case 'pointerdown':
        if (points.length === 1) {
          // starting the polygon
          staging.prepare(true);
          const action = createAction('add', node as FShallowNode);
          staging.add(action);
          selector.current = action.payload.selector;
        }

        if (selector.current && points.length > 0) {
          polygonPointsLength.current = points.length;
          staging.add(applyPolygonPoints(points));
        }
        break;

      default:
        break;
    }
  });

  const onFcarpetClick = useEventCallback((fcarpet: Vector2Tuple[]) => {
    const actions = [];
    const action = createAction('add', node as FShallowNode);
    staging.add(action);
    const selector = action.payload.selector!;

    const { center, size, mask } = maskAndSizeFromPoints(fcarpet);
    actions.push(createAction('update', selector, 'position', center));
    actions.push(createAction('update', selector, 'attrs.size', size));
    actions.push(createAction('update', selector, 'attrs.mask', mask));
    staging.add(actions);
    staging.commit(CommanderActionTypesEnum.Add);
    setDrawFsetup({ node: null, tool: DrawFsetupToolModeEnum.Rectangle });
    set({
      showNavMesh: false,
      tool: ToolModeEnum.None,
    });
  });

  useEffect(() => {
    if (drawFsetupTool === DrawFsetupToolModeEnum.Fill) {
      if (fcarpets === null) {
        const outline = getNavMeshOutline(navMesh);
        setFcarpets(outline);
      }
    }
  }, [drawFsetupTool, fcarpets, navMesh, navMeshQuery]);

  return (
    <>
      {drawFsetupTool === DrawFsetupToolModeEnum.Rectangle && (
        <RectDrawControls color="#6c81d3" navMeshQuery={navMeshQuery} onDraw={handleDraw} />
      )}
      {drawFsetupTool === DrawFsetupToolModeEnum.Polygon && node && (
        <PolygonDrawControls onDraw={onDrawPolygon} />
      )}
      {drawFsetupTool === DrawFsetupToolModeEnum.Fill &&
        fcarpets?.length &&
        fcarpets.map((polygon, i) => (
          <Polygon
            key={i}
            color={hoveredFcarpet === i ? '#00ff00' : '#b3d8fd'}
            cursor="pointer"
            hovered={hoveredFcarpet === i}
            lineWidth={1}
            points={polygon}
            onClick={() => onFcarpetClick(polygon)}
            onPointerLeave={() => {
              if (hoveredFcarpet === i) setHoveredFcarpet(null);
            }}
            onPointerOver={() => setHoveredFcarpet(i)}
          />
        ))}
    </>
  );
};

export const DrawFsetup = () => {
  const tool = useStore(({ tool }) => tool);
  return tool === ToolModeEnum.DrawFsetup ? <DrawFsetupControls /> : null;
};

export default DrawFsetup;
